<script setup>
import api from '@shop/frontend/web/js/api';
import { ref, computed, watch, inject } from 'vue';
import IModal from '@components/IModal/IModal.vue';
import AdditionalServiceItem from './AdditionalServiceItem.vue';
import { Modal } from '@shop/frontend/web/js/components/Modal';
import { translator } from '@shop/frontend/web/js/services/TranslatorService';
import useCartStore from "@vueApp/store/modules/cart";

const cartStore = useCartStore();

const props = defineProps({
  chosenOrder: {
    type: [Object, null],
    required: true,
  },
  refundData: {
    type: [Array, null],
    required: true,
  },
  additionalServices: {
    type: Array,
    required: true,
  },
  additionalTickets: {
    type: Array,
    required: true,
  },
  itemOrderId : {
    type: Number,
    default: 0,
  },
  itemParentId: {
    type: String,
    default: '',
  },
  itemUid: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['successRefund', 'update-carts']);
const localAdditionalServices = ref([]);

const refundSuccessModal = new Modal({
  className: 'i-modal_refund-success',
});

async function sendAdditionalService() {
  const selectedServices = localAdditionalServices.value
    .filter(service => service.addAdditionalTicketCount > 0)
    .flatMap(service => 
      Array.from({ length: service.addAdditionalTicketCount }, () => ({
        orderId: service.itemOrderId,
        parent: service.itemParentId,
        serviceId: service.service_id,
        priceTypeId: service.view_id,
        quantity: 1,
      }))
    );

  let additionalServicesPayload = {
    items: selectedServices,
  };

  if(selectedServices.length === 0) {
    additionalServicesPayload = {
      items: [{
        orderId: props.itemOrderId,
        parent: props.itemParentId,
        serviceId: null,
        priceTypeId: null,
      }]
    }
  }
  
  const jsonPayload = JSON.stringify(additionalServicesPayload);

  try {
    const res = await api.additionalServices.addAdditionalServices(jsonPayload);

    if (!res?.carts?.length) {
      throw new Error("Response does not contain 'carts' or it's empty");
    }

    const lastCartItem = res.carts[res.carts.length - 1];
    const itemsToSearch = Array.isArray(lastCartItem) ? lastCartItem.flat() : [lastCartItem];
    const currentСart = itemsToSearch.find(item => item.uid === props.itemUid)?.additionalTickets;

    emit('update-carts', currentСart);
    await cartStore.loadCart();
  } catch (e) {
    refundSuccessModal.show('Не удалось добавить услугу...');
  }
}

watch(
  () => props.additionalServices,
  (newServices) => {
    if (!newServices || !Array.isArray(newServices)) {
      localAdditionalServices.value = [];
      return;
    }

    localAdditionalServices.value = newServices.map((service) => {
      const addedServiceTickets = (props.additionalTickets || []).filter((ticket) =>
        ticket.price === service.price &&
        ticket.view_id === service.view_id &&
        ticket.service_id === service.service_id
      ).length;

      return {
        ...service,
        itemOrderId: props.itemOrderId,
        itemParentId: props.itemParentId,
        addAdditionalTicketCount: addedServiceTickets|| service.addAdditionalTicketCount || 0,
      };
    });
  },
  { immediate: true, deep: true }
);


function updateTicketCount(serviceId, viewId, newCount) {
  const service = localAdditionalServices.value.find(s => s.service_id === serviceId && s.service_id === viewId);
  if (service) {
    service.addAdditionalTicketCount = newCount;
  }
}

const showScheme = ref(false)
const serviceId = ref(null)
const bigModal = ref(false)

function changeShowScheme(value) {
  showScheme.value = !showScheme.value;
  serviceId.value = value;
  bigModal.value = false;

  if(value === 226 || value === 37) {
    bigModal.value = !bigModal.value;
  }
}

let parkingScheme = {
  224: '/images/parking-scheme/parking-1.jpg',
  225: '/images/parking-scheme/parking-2.jpg',
  226: '/images/parking-scheme/parking-3.jpg',
  35: '/images/parking-scheme/parking-1.jpg',
  36: '/images/parking-scheme/parking-2.jpg',
  37: '/images/parking-scheme/parking-3.jpg'
}

</script>

<template>  
  <IModal :bigModal="bigModal" class="additional-service">
    <template #header>
      <span v-if="showScheme" class="additional-service__header">
        <span @click="changeShowScheme" class="additional-service__back-link">
          <svg width="20" height="20" aria-hidden="true" focusable="false">
            <use xlink:href="#additional-service-back"></use>
          </svg>
          <span>
            {{ translator.t("main", "Back") }}
          </span>
        </span>
      </span>
      <span v-else class="additional-service__header additional-service-modal__header">
        {{ translator.t("main", "Additional services") }}
      </span>
    </template>
    <template #body>
    <div v-if="showScheme">
      <img 
        class="additional-service__scheme-img" 
        v-show="showScheme" 
        :src="parkingScheme[serviceId]" 
        alt="">
    </div>
    <div v-else>
      <div class="additional-service__body additional-service-modal__desc">
        <p class="additional-service__text">
          {{ translator.t("main", "You can select additional services from the list.") }}
        </p>
        <p class="additional-service__text">
          {{ translator.t("main", "You can only get a refund for additional services if you return the ticket in full. You cannot partially refuse services.") }}
        </p>
      </div>

      <div class="additional-service__tickets-wrapper additional-service-modal">
        <ul class="additional-services__container">
          <AdditionalServiceItem
            v-for="ticket in localAdditionalServices"
            :key="ticket.ticketUid"
            :ticket="ticket"
            class="additional-service__place additional-service"
            v-model="ticket.addAdditionalTicketCount"
            @update:modelValue="(newValue) => updateTicketCount(ticket.service_id, ticket.view_id, newValue)"
          />
        </ul>
      </div>
      <div class="additional-service-modal__footer">
        <button
          type="button" class="btn" data-bs-dismiss="modal"
          :disabled="false"
          @click="sendAdditionalService"
        >
        {{ translator.t("main", "Add") }}
        </button>
      </div>
    </div>
    </template>
  </IModal>
</template>

<style lang="scss">
@import '../../../../styles/custom/additional-services.scss';

button:disabled {
  opacity: 0.5;
}

.additional-service {
  .btn-close {
    position: absolute;
    color: $primary;
    top: 16px;
    right: 16px;
  }

  &__back-link {
    display: flex;
    gap: 8px;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    position: absolute;
    top: 16px;
    left: 16px;

    span {
      font-family: Roboto;
      font-size: 14px;
      font-style: normal;
      font-weight: 400;
      line-height: 120%;
      color: $main;
    }
  }

  .modal-content {
    max-width: 496px;
    border: none;
  }

  .modal-header {
    padding: 48px 48px 0 48px;
    margin-bottom: 16px;
  }

  .modal-body {
    padding: 0 48px 48px;
  }

  .additional-service__footer {
    width: 100%;

    .btn {
      padding: 9.5px 18px;
      margin: 0;
      width: 100%;
      font-weight: 500;
    }
  }

  &__place-old-price {
    text-decoration: line-through;
    margin-right: 16px;
  }

  &__header {
    font-family: Roboto;
    font-size: 24px;
    font-weight: 700;
  }

  &__text {
    font-family: Roboto;
    position: relative;
    margin-bottom: 12px;
    font-size: 14px;

    &:last-child {
      margin-bottom: 16px;
    }
  }

  &__places-list {
    padding: 0;
    margin-top: 10px;
    margin-bottom: 20px;
    list-style: none;
  }

  &__place {
    display: flex;
    align-items: center;
    justify-content: space-between;
    text-transform: capitalize;

    &:not(&:last-of-type) {
      margin-bottom: 12px;
    }
  }

  &__place_disabled {
    pointer-events: none;
    opacity: 0.5;

    .custom-checkbox {
      opacity: 0.5;
    }
  }

  &__price_selected {
    color: $grey-text;
  }

  &__agree-label {
    display: flex;
    align-items: center;
    gap: 11px;

    .custom-checkbox-input:checked ~ .custom-checkbox:after {
      opacity: 1;
    }
  }

  &__agree-label_sum {
    margin-bottom: 12px;
  }

  &__event_disabled {
    opacity: 0.5;
    pointer-events: none;
  }

  &__event-name {
    display: inline-block;
    margin-bottom: 10px;
    font-weight: 700;
    font-size: 18px;
  }

  &__tickets-wrapper {
    margin-bottom: 16px;
    padding: 16px 0 0;
  }

  &__info {
    display: block;
    font-size: 14px;
    color: $error;

    mark {
      background-color: transparent;
      text-decoration: underline;
      cursor: pointer;
    }
  }

  @media (max-width: $screen-xs) {
    .modal-dialog {
      margin: 0;
      height: 100%;
    }

    .modal-content {
      border-radius: 0;
      height: 100%;
      overflow-y: auto;
    }

    .modal-header {
      padding: 24px 16px 0;
    }

    .modal-body {
      padding: 0 16px 16px;
      flex: 0;
    }

    &__header {
      font-weight: 600;
      font-size: 20px;
    }

    &__text {
      margin-bottom: 16px;
      line-height: 19.6px;

      &:last-of-type {
        margin-bottom: 20px;
      }
    }
  }
}

.additional-service-modal {
  &__list {
    margin-top: 16px;
    margin-bottom: 24px;
    display: flex;
    flex-direction: column;
    row-gap: 8px;
  }

  &__footer {
    .btn {
      width: 100%;
      font-family: 'Montserrat';
      font-size: 16px;
      font-style: normal;
      font-weight: 500;
      line-height: 130%; 
      display: flex;
      height: 40px;
      padding: 12px 24px;
      justify-content: center;
      align-items: center;
      background: $primary;
      border-radius: 5px;
      color: $white;
      width: 100%;
    }
  }
}
</style>